import { useEffect, useMemo, useState } from 'react';
import {
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { MdInventory2, MdRefresh } from 'react-icons/md';
import { LuMessageCircle } from 'react-icons/lu';
import { Page } from '@components/layout/Page';
import { PageEmpty } from '@components/layout/PageEmpty';
import { DataPagination } from '@components/ui/DataPagination';
import { GridSkeleton } from '@components/ui/GridSkeleton';
import { TabFilter } from '@components/ui/TabFilter';
import { todayIso } from '@utils/format';
import { PriceReviewGrid } from '@features/priceReview/components/PriceReviewGrid';
import { ApproveDialog } from '@features/priceReview/components/ApproveDialog';
import { RejectDialog } from '@features/priceReview/components/RejectDialog';
import { useObservationStaging } from '@features/priceReview/hooks';
import {
  RESOLUTION_STATUSES,
  type ListStagingParams,
  type ResolutionStatus,
  type SortDirection,
  type StagingRow,
  type StagingSortKey,
} from '@features/priceReview/schema';

const DEFAULT_PAGE_SIZE = 50;

// Top-right switch: the pending review queue vs. the rows already published.
type ReviewView = 'pending' | 'published';
const VIEWS: ReadonlyArray<{ value: ReviewView; label: string }> = [
  { value: 'pending', label: 'Pending queue' },
  { value: 'published', label: 'Published' },
];
// Published view = rows a reviewer already actioned into a published price.
const VIEW_STATUSES: Record<ReviewView, string> = {
  pending: 'PENDING',
  published: 'APPROVED,EDITED',
};

export default function PriceReviewScreen() {
  const [view, setView] = useState<ReviewView>('pending');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [date, setDate] = useState(todayIso());
  const [resolution, setResolution] = useState<ResolutionStatus | ''>('');
  const [sort, setSort] = useState<{
    sortBy?: StagingSortKey;
    sortDir?: SortDirection;
  }>({});
  const [approvingRow, setApprovingRow] = useState<StagingRow | null>(null);
  const [rejectingRow, setRejectingRow] = useState<StagingRow | null>(null);

  useEffect(() => {
    setPage(1);
  }, [date, resolution, pageSize, sort, view]);

  const queueParams = useMemo<ListStagingParams>(
    () => ({
      page,
      pageSize,
      date: date || undefined,
      statuses: VIEW_STATUSES[view],
      resolutionStatus: resolution || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortDir,
    }),
    [page, pageSize, date, view, resolution, sort],
  );

  const queue = useObservationStaging(queueParams);
  const totalRecords = queue.data?.paginationDetails?.totalRecords ?? 0;
  const isInitialLoad = queue.isLoading && !queue.data;
  const showEmpty = !queue.isLoading && (queue.data?.rowData?.length ?? 0) === 0;

  return (
    <Page
      title="Price review"
      subtitle="WhatsApp-ingested mandi prices awaiting review before they publish."
      fullHeight
      headerRightSlot={
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-success/[0.12] px-2.5 py-1 text-[12px] font-semibold text-success">
            <LuMessageCircle size={14} />
            WhatsApp pipeline
          </span>
          <TabFilter
            options={VIEWS}
            current={view}
            onChange={setView}
            ariaLabel="Price review view"
          />
        </div>
      }
    >
      <div className="mb-3 flex flex-col gap-3 rounded-xl bg-surface-muted px-3 py-3 md:flex-row md:flex-wrap md:items-end md:px-4">
        <TextField
          label="Date"
          type="date"
          size="small"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          className="md:w-[170px]"
        />
        <TextField
          label="Resolution"
          size="small"
          select
          value={resolution}
          onChange={(e) => setResolution(e.target.value as ResolutionStatus | '')}
          className="md:w-[170px]"
        >
          <MenuItem value="">All</MenuItem>
          {RESOLUTION_STATUSES.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Page size"
          size="small"
          select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="md:w-[95px]"
        >
          {[25, 50, 100].map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>

        <div className="flex items-center gap-3 md:ml-auto">
          <Tooltip title={queue.isFetching ? 'Loading…' : 'Refresh'}>
            <span>
              <IconButton
                onClick={() => queue.refetch()}
                size="small"
                disabled={queue.isFetching}
              >
                {queue.isFetching ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <MdRefresh />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-xl border border-border bg-surface">
        {queue.isFetching && queue.data && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 5,
              height: 2,
            }}
          />
        )}
        {isInitialLoad ? (
          <GridSkeleton
            rows={6}
            columns={[
              { width: '150px', bar: 'w-3/4' },
              { width: '150px', bar: 'w-2/3' },
              { width: '2fr', bar: 'w-3/4' },
              { width: '120px', bar: 'w-14' },
              { width: '150px', bar: 'w-16' },
              { width: '170px', bar: 'w-16' },
              { width: '150px', empty: true },
            ]}
          />
        ) : showEmpty ? (
          <PageEmpty
            icon={MdInventory2}
            title={
              view === 'published' ? 'Nothing published yet' : 'Nothing to review'
            }
            message={
              view === 'published'
                ? 'No approved/published rows for this date. Approve pending rows and they will appear here.'
                : 'No pending WhatsApp price rows for this date. Try another date, or check back when the pipeline ingests new photos.'
            }
          />
        ) : (
          <PriceReviewGrid
            data={queue.data}
            onSortChange={setSort}
            onView={setApprovingRow}
            onApprove={setApprovingRow}
            onReject={setRejectingRow}
          />
        )}
      </div>

      <DataPagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
      />

      <ApproveDialog
        open={!!approvingRow}
        row={approvingRow}
        onClose={() => setApprovingRow(null)}
      />
      <RejectDialog
        open={!!rejectingRow}
        row={rejectingRow}
        onClose={() => setRejectingRow(null)}
      />
    </Page>
  );
}
